import Component from '../abstracts/Component';
import TrackingComponent from './TrackingComponent';
import WishlistLogic from '../logics/WishlistLogic';
import vex from 'vex-js';
import { removeAlgoliaAttributes } from '../utils/algoliaTrackings';

export default class PdpMainComponent extends Component {

  get SELECTORS() {
    return {
      wishlistButton: "[data-js-wishlist]",
      infoSection: "[data-js-info]",
      sizeGuideTrigger: "[data-js-size-guide]",
      swatchBullet: "[data-js-swatch]",
      swatchColor: "[data-js-swatch-color]",
      // pdpCta/chatCta interactions
      pdpCtaMobile: "[data-pdp-cta].--mobile",
      pdpNotifyMeMobile: "[data-pdp-notifyme].--mobile",
      chatCta: '.embeddedServiceHelpButton.livechat-button .helpButton',
      newChatCta: '.embeddedMessagingConversationButton',
    };
  }

  get CLASSES() {
    return {
      iconWishlistEmpty: "icon-wishlist-empty",
      iconWishlistFull: "icon-wishlist-full",
    };
  }

  constructor(compEl) {
    super(compEl);
    this.trackingComponent = new TrackingComponent();
    this.wishlistLogic = new WishlistLogic();
  }

  readDOM() {
    return {
      wishlistButton: this.$component.querySelector(this.SELECTORS.wishlistButton),
      infoSection: this.$component.querySelector(this.SELECTORS.infoSection),
      sizeGuideTrigger: this.$component.querySelector(this.SELECTORS.sizeGuideTrigger),
      swatchBullets: this.$component.querySelectorAll(this.SELECTORS.swatchBullet),
      swatchColor: this.$component.querySelector(this.SELECTORS.swatchColor),
      // pdpCta/chatCta interactions
      pdpCtaMobile: this.$component.querySelector(this.SELECTORS.pdpCtaMobile),
      pdpNotifyMeMobile: this.$component.querySelector(this.SELECTORS.pdpNotifyMeMobile),
      newChatCta: this.$component.querySelector(this.SELECTORS.newChatCta),
    };
  }

  // Handle wishlist add //
  handleWishlist() {
    if (this.dom.wishlistButton) {

      // update wishlist button for sizes
      this.$on(this.$customEvents.PRODUCT_EVENTS.sizechanged, ({payload}) => {
        const isFull = payload.isWishlist === 'full';
        // update url
        this.dom.wishlistButton.dataset.code = payload.productCode;
        // get size wishlist status
        this.dom.wishlistButton.dataset.jsWishlist = isFull ? 'full' : 'empty';
        // update size after add/remove success
        this.dom.wishlistButton.dataset.isSize = 'true';
        // update icon class
        const event = isFull ? this.$customEvents.PRODUCT_EVENTS.addedtowish : this.$customEvents.PRODUCT_EVENTS.removedfromwish;
        this.$emit(event, { elem: this.dom.wishlistButton });
      });

      this.dom.wishlistButton.addEventListener("click", () => {
        const addUrl = this.dom.wishlistButton.dataset.addUrl;
        const removeUrl = this.dom.wishlistButton.dataset.removeUrl;
        const productCode = this.dom.wishlistButton.dataset.code;
        const isAdding = this.dom.wishlistButton.dataset.jsWishlist === "empty";
        const isSize = this.dom.wishlistButton.dataset.isSize === 'true';
        const addedMessage = this.dom.wishlistButton.dataset.addedMessage;
        const removedMessage = this.dom.wishlistButton.dataset.removedMessage;
        const code = this.dom.wishlistButton.dataset.code;
        const url = (isAdding ? addUrl : removeUrl) + code;
        fetch(url, { method: 'POST' })
          .then(response => {
          return response.json();
        })
          .then(text => {
          if (isAdding) {
            this.dom.wishlistButton.dataset.jsWishlist = "full";
            this.$emit(this.$customEvents.PRODUCT_EVENTS.addedtowish, { qty: text, elem: this.dom.wishlistButton, msg: addedMessage });
            this.trackingComponent.trackAction('wishlistAdd', {code: productCode});
          } else {
            this.dom.wishlistButton.dataset.jsWishlist = "empty";
            this.$emit(this.$customEvents.PRODUCT_EVENTS.removedfromwish, { qty: text.count, elem: this.dom.wishlistButton, msg: removedMessage });
          }
          if (isSize) {
            // update size for next interaction
            this.$emit(this.$customEvents.SIZES_EVENTS.sizesupdate, {
              code: productCode,
              isWishlist: this.dom.wishlistButton.dataset.jsWishlist
            });
          }
        })
          .catch(error => this.warn(error));
      });
    }
  }

  // on desktop the left info bar is sticky to the top of the container //
  setInfoSectionSticky() {
    if (window.matchMedia('(min-width: 768px)').matches) {
      const header = document.querySelector('[data-header-menu]');
      if (header) {
        const height = header.offsetHeight;
        this.dom.infoSection.style.top = height + "px";
      } else {
        this.dom.infoSection.style.top = '0';
      }
    }
  }

  scrollInfoSection() {
    if (window.matchMedia('(min-width: 768px)').matches) {
      let previousScrollPosition = 0;
      window.addEventListener("scroll", () => {
        const scrollPosition = window.pageYOffset;
        if (scrollPosition > previousScrollPosition) {
          //scrolling down
          this.dom.infoSection.scroll({
            top: this.dom.infoSection.offsetHeight,
            behavior: "smooth",
          });
        } else {
          //scrolling up
          this.dom.infoSection.scroll({
            top: -this.dom.infoSection.offsetHeight,
            behavior: "smooth",
          });
        }
        previousScrollPosition = scrollPosition;
      });
    }
  }

  // handle size guide open //
  handleSizeGuide() {
    const ctx = this;
    if (this.dom.sizeGuideTrigger) {
      this.dom.sizeGuideTrigger.addEventListener("click", (event) => {
        event.preventDefault();
        this.dom.sizeGuideTrigger.setAttribute('disabled', '');
        fetch(this.dom.sizeGuideTrigger.href)
          .then(response => response.text())
          .then(text => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(text, "text/html");
          const sizeGuide = doc.querySelector('[data-ajax-chunk]');
          vex.open({
            unsafeContent: sizeGuide,
            closeClassName: 'icon-close',
            className: 'size-guide-layer',
            showCloseButton: true,
            afterOpen: function () {
              this.closeEl.classList.remove('vex-close');
              ctx.importComponents("CollapseComponent")
                .then(({default: module}) => {
                const component = new module(sizeGuide);
                component.render();
              })
                .catch(error => {
                console.error("Failed to load the component", error.stack);
              });
            },
            afterClose: function () {
              ctx.dom.sizeGuideTrigger.removeAttribute('disabled');
            }
          });
        })
          .catch(error => console.error(error));
      });
    }
  }

  // Swatches interactions
  handleSwatchHover() {
    if (this.dom.swatchBullets && this.dom.swatchColor) {
      this.dom.swatchBullets.forEach(bullet => {
        bullet.addEventListener("mouseover", () => {
          this.dom.swatchColor.innerHTML = bullet.dataset.jsSwatch;
        });
        bullet.addEventListener("mouseleave", () => {
          this.dom.swatchColor.innerHTML = this.dom.swatchColor.dataset.jsSwatchColor;
        });
      });
    }
  }

  importComponents(componentName) {
    return import(`../components/${componentName}`);
  }

  bindEvents() {
    this.handleWishlist();
    this.handleSizeGuide();
    this.handleSwatchHover();
  }

  render() {
    super.render();
    this.dom = this.readDOM();
    this.setInfoSectionSticky();
    this.scrollInfoSection();
    this.bindEvents();
    this.wishlistLogic.render();
    // If algolia params are stored remove them
    removeAlgoliaAttributes();
  }
}
